// src/pages/HeatmapPage.tsx
import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, useMap, Pane } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { LeafletHeatLayer } from "@/lib/heatmap";
import {
  useFilteredHeatPoints,
  useLgsPoints,
  useHeatLoading,
  useHeatError,
  useLgsError,
  useHeatmapStore,
} from "@/lib/heatmapStore";
import { useHeatmapInitFull } from "@/lib/useHeatmapInit";
import LgsMarkers from "@/components/heatmap/LgsMarkers";
import CurrentLocationDot from "@/components/heatmap/CurrentLocationDot";
import MapAutoResize from "@/components/heatmap/MapAutoResize";
import { HeatmapLegend } from "@/components/heatmap/HeatmapLegend";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import { publishUserHeatPing } from "@/lib/publishHeatPing";

/* Error boundary (unchanged behavior) */
class LocalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err: any) {
    console.error("Heatmap page error", err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-sm text-red-400">
          Something went wrong loading the map.
        </div>
      );
    }
    return this.props.children;
  }
}

function MapRefBinder() {
  const map = useMap();
  useEffect(() => {
    (window as any)._mainHeatmapMap = map;
  }, [map]);
  return null;
}

function CenterOnMeButton() {
  return (
    <button
      onClick={() => {
        const map: any = (window as any)._mainHeatmapMap;
        if (!map || !("geolocation" in navigator)) return;
        navigator.geolocation.getCurrentPosition(
          (p) => {
            publishUserHeatPing(p.coords.latitude, p.coords.longitude, "manual").catch(() => {});
            const attempt = () => {
              if (map && map._loaded) {
                try {
                  map.setView(
                    [p.coords.latitude, p.coords.longitude],
                    Math.max(map.getZoom(), 11),
                    { animate: true }
                  );
                  useHeatmapStore.getState().fetchNearbyLgs({
                    lat: p.coords.latitude,
                    lng: p.coords.longitude,
                    force: true,
                  });
                } catch {}
              } else {
                requestAnimationFrame(attempt);
              }
            };
            requestAnimationFrame(attempt);
          },
          () => {}
        );
      }}
      className="px-3 py-1.5 text-[12px] rounded bg-neutral-900/80 border border-neutral-700 text-neutral-200 hover:bg-neutral-800"
      title="Center on my location"
    >
      Me
    </button>
  );
}

function AutoCenterOnLoadFull({ points }: { points: number[][] }) {
  const map = useMap();
  useEffect(() => {
    let canceled = false;
    let centered = false;

    const fitHeatBounds = () => {
      if (canceled || centered || !points.length) return;
      try {
        const L = (window as any).L;
        if (!L) return;
        const b = L.latLngBounds(points.map((p) => [p[0], p[1]]));
        if ((map as any)?._loaded) {
          map.fitBounds(b.pad(0.3));
          centered = true;
        }
      } catch {}
    };

    const geoSuccess = (p: GeolocationPosition, mode: "login" | "manual") => {
      if (canceled || centered) return;
      const { latitude, longitude } = p.coords;
      publishUserHeatPing(latitude, longitude, mode).catch(() => {});
      const attempt = () => {
        if (canceled || centered) return;
        if ((map as any)?._loaded) {
          try {
            map.setView([latitude, longitude], Math.max(map.getZoom(), 11), {
              animate: true,
            });
            centered = true;
            useHeatmapStore
              .getState()
              .fetchNearbyLgs({ lat: latitude, lng: longitude, force: true });
          } catch {}
        } else {
          requestAnimationFrame(attempt);
        }
      };
      requestAnimationFrame(attempt);
    };

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => geoSuccess(pos, "login"),
        () => {
          setTimeout(fitHeatBounds, 200);
        },
        { enableHighAccuracy: true, timeout: 6000 }
      );
      setTimeout(() => {
        if (!centered) fitHeatBounds();
      }, 1600);
    } else {
      fitHeatBounds();
    }

    return () => {
      canceled = true;
    };
  }, [map, points]);

  return null;
}

export default function HeatmapPage() {
  const navigate = useNavigate();

  // Auth gate
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  // Call hook at top level with enable flag
  const enableHeatmap = authReady && !!user;
  useHeatmapInitFull(enableHeatmap);

  const filteredTriplets = useFilteredHeatPoints();
  const heatLoading = useHeatLoading();
  const heatError = useHeatError();
  const lgsError = useLgsError();
  const lgsPoints = useLgsPoints();
  const heatCells = useHeatmapStore((s) => s.heatPoints.length);
  const syncingLgs = useHeatmapStore((s) => s._lgsSyncInFlight);

  const [showNotice, setShowNotice] = useState(false);

  // Convert triplets to heat layer format [lat,lng,w]
  const heatPoints = useMemo(
    () => (filteredTriplets?.length ? filteredTriplets : []),
    [filteredTriplets]
  );

  const faded = heatLoading || !!heatError || !!lgsError;

  // Loading / auth states
  if (!authReady) {
    return (
      <div className="p-6 text-sm text-neutral-400">Loading session…</div>
    );
  }
  if (authReady && !user) {
    return (
      <div className="p-6 text-sm text-neutral-400">
        Please sign in to view the heatmap.
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Top controls */}
      <div className="mb-2 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <CenterOnMeButton />
          <button
            onClick={() => useHeatmapStore.getState().refreshLgs()}
            className="px-3 py-1.5 text-[12px] rounded bg-neutral-900/80 border border-neutral-700 text-neutral-200 hover:bg-neutral-800"
            title="Refresh nearby LGS"
          >
            Refresh LGS
          </button>
          <button
            onClick={() => setShowNotice((v) => !v)}
            className="px-3 py-1.5 text-[12px] rounded bg-neutral-900/80 border border-neutral-700 text-neutral-200 hover:bg-neutral-800"
            title="Toggle heatmap notice"
          >
            {showNotice ? "Hide Notice" : "Show Notice"}
          </button>
        </div>
        <div className="text-[11px] text-neutral-500 shrink-0">
          {heatCells} pts · {lgsPoints.length} stores
        </div>
      </div>

      {showNotice && (
        <div className="mb-3 text-[11px] leading-snug text-neutral-300 bg-neutral-900/70 border border-neutral-700 rounded p-2">
          Player heatmap data is approximate & aggregated — it never reveals
          exact locations. Local Game Store markers are from public sources and
          may be outdated.
        </div>
      )}

      <div className="relative h-[calc(100dvh-170px)] w-full">
        <LocalErrorBoundary>
          <MapContainer
            center={[39.5, -98.35]}
            zoom={4}
            minZoom={2}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
            preferCanvas
            className={`transition-opacity duration-500 ${
              faded ? "opacity-50" : "opacity-100"
            }`}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution="&copy; OpenStreetMap & CARTO"
            />
            <MapRefBinder />
            <MapAutoResize />
            <CurrentLocationDot />
            <AutoCenterOnLoadFull points={heatPoints} />
            {/* Heat + markers */}
            {heatPoints.length > 0 && (
              <Pane
                name="heatmap-pane"
                style={{ zIndex: 350, pointerEvents: "none" }}
              >
                <LeafletHeatLayer
                  points={heatPoints}
                  radius={30}
                  blur={22}
                  maxZoom={14}
                  // ensure the canvas never blocks marker clicks
                  interactive={false}
                  pane="heatmap-pane"
                />
              </Pane>
            )}
            {/* LGS markers stay on default markerPane (zIndex 600), above the heat */}
            <LgsMarkers />
          </MapContainer>

          {/* Overlays */}
          <div className="pointer-events-none absolute inset-0 z-[1000]">
            <div className="absolute top-3 left-3 flex gap-3 pointer-events-auto">
              <HeatmapLegend />
            </div>
            <div className="absolute top-3 right-3 flex gap-2 pointer-events-auto">
              <button
                onClick={() => {
                  if (window.history.length > 1) navigate(-1);
                  else navigate("/dashboard");
                }}
                className="px-3 py-1.5 text-[12px] rounded bg-neutral-900/80 border border-neutral-700 text-neutral-200 hover:bg-neutral-800"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {heatError && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1200] px-3 py-2 rounded bg-red-900/70 border border-red-600 text-red-200 text-xs">
              {heatError}
            </div>
          )}
          {lgsError && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1200] px-3 py-2 rounded bg-red-900/70 border border-red-600 text-red-200 text-xs">
              {lgsError}
            </div>
          )}
          {syncingLgs && (
            <div className="absolute top-28 left-1/2 -translate-x-1/2 z-[1200] px-3 py-1.5 rounded bg-neutral-900/70 border border-neutral-700 text-neutral-300 text-[11px]">
              Syncing LGS…
            </div>
          )}
          {heatPoints.length === 0 &&
            !heatLoading &&
            !heatError &&
            !lgsError && (
              <div className="absolute inset-0 flex items-center justify-center text-[12px] text-neutral-500 pointer-events-none">
                No player heat data
              </div>
            )}
        </LocalErrorBoundary>
      </div>
    </div>
  );
}

